// apps/web/src/lib/offline/sync-regras.ts — Fase 11 (CR-WEB-01).
// Regras puras da sincronização do outbox (sem Dexie, sem rede): classificação das
// respostas do servidor, sequência monotônica por dispositivo e intervalo de retry.
import { devePurgarCredencial } from '../kanban/dispositivo';

export type AcaoRespostaSync =
  /** 2xx: o lote foi aceito (verificar ids devolvidos). */
  | 'ok'
  /** 401 revoked/invalid/expired: a credencial local não serve mais. */
  | 'purgar_credencial'
  /** 4xx definitivo para este evento (400/404/409/413/422): não adianta reenviar. */
  | 'rejeitar_evento'
  /** 401 sem motivo de dispositivo ou 403: sessão expirada/sem permissão; reenviar depois do login. */
  | 'tentar_depois_autenticacao'
  /** 5xx, 408, 425, 429 e demais: instabilidade; reenviar com backoff. */
  | 'tentar_depois';

const STATUS_REJEICAO_DEFINITIVA = new Set([400, 404, 409, 413, 422]);

export function classificarRespostaSync(status: number, reason?: string | null): AcaoRespostaSync {
  if (status >= 200 && status < 300) return 'ok';
  if (devePurgarCredencial({ status, reason: reason ?? undefined })) return 'purgar_credencial';
  if (status === 401 || status === 403) return 'tentar_depois_autenticacao';
  if (STATUS_REJEICAO_DEFINITIVA.has(status)) return 'rejeitar_evento';
  return 'tentar_depois';
}

/**
 * Próxima deviceSequence: sempre acima do contador persistido e de qualquer
 * sequência já presente no outbox do dispositivo (o relógio de parede não entra).
 */
export function proximaSequencia(contadorPersistido?: number | null, maiorNoOutbox?: number | null): number {
  return Math.max(contadorPersistido ?? 0, maiorNoOutbox ?? 0) + 1;
}

export const INTERVALO_RETRY_BASE_MS = 10_000;
export const INTERVALO_RETRY_MAX_MS = 5 * 60_000;
export const INTERVALO_OCIOSO_MS = 30_000;

/** Backoff exponencial com teto: 10 s, 20 s, 40 s ... 5 min. Sem falhas, verificação ociosa. */
export function intervaloRetryMs(falhasConsecutivas: number, haPendentes: boolean): number {
  if (!haPendentes) return INTERVALO_OCIOSO_MS;
  const falhas = Math.max(0, Math.floor(falhasConsecutivas));
  return Math.min(INTERVALO_RETRY_BASE_MS * 2 ** Math.min(falhas, 16), INTERVALO_RETRY_MAX_MS);
}

/** Ordem de envio: deviceSequence crescente (desempate por criação). */
export function ordenarParaEnvio<T extends { deviceSequence: number; createdAt: number }>(eventos: readonly T[]): T[] {
  return [...eventos].sort((a, b) => a.deviceSequence - b.deviceSequence || a.createdAt - b.createdAt);
}
